import type { Recommendation } from '../types/case';
import { formatDate } from '../lib/format';

interface Props {
  recommendations: Recommendation[];
}

const statusLabel = (status: string): string => {
  switch (status) {
    case 'pending_review':
      return 'Pending review';
    case 'sent_to_parties':
      return 'Sent to parties';
    case 'accepted':
      return 'Accepted';
    case 'rejected':
      return 'Rejected';
    case 'expired':
      return 'Expired';
    case 'superseded':
      return 'Superseded';
    default:
      return status;
  }
};

const statusColor = (status: string): string => {
  switch (status) {
    case 'accepted':
      return 'text-green-700';
    case 'rejected':
    case 'expired':
      return 'text-red-700';
    case 'sent_to_parties':
      return 'text-blue-700';
    default:
      return 'text-gray-600';
  }
};

export function NegotiationHistory({ recommendations }: Props) {
  if (recommendations.length === 0) {
    return (
      <section className="card p-4">
        <div className="section-label">Negotiation history</div>
        <p className="mt-2 text-sm text-gray-500">No prior proposals.</p>
      </section>
    );
  }

  const sorted = [...recommendations].sort(
    (a, b) => new Date(a.generated_at).getTime() - new Date(b.generated_at).getTime(),
  );

  return (
    <section className="card p-4">
      <div className="section-label">Negotiation history</div>
      <ol className="mt-3 space-y-3 text-sm">
        {sorted.map((rec) => (
          <li key={rec.id} className="border-l-2 border-surface-border pl-3">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{formatDate(rec.generated_at)}</span>
              <span className={`text-xs font-medium ${statusColor(rec.status)}`}>
                {statusLabel(rec.status)}
              </span>
            </div>
            <div className="mt-1 text-xs uppercase tracking-wide text-gray-500">{rec.type}</div>
            <p className="mt-1 text-gray-700">{rec.reasoning}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}
